import { PC_BREAK_POINT, IS_RTL_SERVICE } from '../constant';
import {
    ScrollState,
    mousedownEventHandler,
    mouseleaveHandler,
    mousemoveHandler,
    mouseupEventHandler,
} from './ScrollEvent';
/**
 * @category tab
 * @description 가로 스크롤 drag & drop 기능 이벤트 등록 함수
 * @param {HTMLElement} horizontalScroll 가로 스크롤 되는 영역
 * @returns void
 * @example
 * setHorizontalScrollEvent(document.querySelector('.depth3-list'));
 */
export const setHorizontalSrollEvent = (
    horizontalScroll: HTMLElement
): void => {
    if (!(horizontalScroll instanceof HTMLElement)) return;
    const scroll = new ScrollState();

    horizontalScroll.addEventListener('mousedown', (event) =>
        mousedownEventHandler(scroll, event, horizontalScroll)
    );
    horizontalScroll.addEventListener('mouseleave', () =>
        mouseleaveHandler(scroll)
    );
    horizontalScroll.addEventListener('mousemove', (event) =>
        mousemoveHandler(scroll, event, horizontalScroll)
    );
    horizontalScroll.addEventListener('mouseup', () =>
        mouseupEventHandler(scroll, horizontalScroll)
    );
};

/**
 * @category tab
 * @description 가로 스크롤 drag & drop 기능 이벤트 등록 함수
 * @param {HTMLElement} horizontalScroll 가로 스크롤 되는 영역
 * @returns void
 * @example
 * setHorizontalScrollEvent(document.querySelector('.depth3-list'));
 */
export const scrollToActivatedTab = (
    activatedTab: HTMLElement,
    activatedTabIndex: number,
    toBeScrolledElement: HTMLElement
): void => {
    if (
        window.innerWidth > PC_BREAK_POINT ||
        !(activatedTab instanceof HTMLElement) ||
        !(toBeScrolledElement instanceof HTMLElement)
    )
        return;
    toBeScrolledElement.scrollLeft = getTabWidth(
        activatedTab,
        activatedTabIndex,
        1,
        IS_RTL_SERVICE
    );
};

/**
 * @category tab
 * @description 화면 크기가 변경될 때 활성화된 탭을 보여주는 이벤트 등록
 * @param {HTMLElement} horizontalScroll 가로 스크롤 되는 영역
 * @param {HTMLElement} activateTab 활성화된 탭
 * @param {number} activatedTabIndex 활성화된 탭 Index
 * @example
 * setResizeEvent(document.querySelector('.depth3-list'), document.querySelector('.depth3-list-items.selected'), true);
 */
export const setResizeEvent = (
    horizontalScroll: HTMLElement,
    activatedTab: HTMLElement,
    activatedTabIndex: number
): void => {
    const lastTab = horizontalScroll.lastElementChild;
    if (!(lastTab instanceof HTMLElement)) return;

    const previousActivatedTabX = getTabWidth(
        activatedTab,
        activatedTabIndex,
        1,
        IS_RTL_SERVICE
    );

    window.addEventListener('resize', () =>
        _resizeEventHandler(
            lastTab,
            horizontalScroll,
            IS_RTL_SERVICE,
            previousActivatedTabX,
            activatedTab,
            activatedTabIndex,
            0,
            0
        )
    );
};

// resize 이벤트 핸들러 함수
const _resizeEventHandler = (
    lastTab: HTMLElement,
    horizontalScroll: HTMLElement,
    isRTLService: boolean = false,
    previousActivatedTabX: number,
    activatedTab: HTMLElement,
    activatedTabIndex: number,
    prevMove: number = 0,
    curMove: number = 0
): void => {
    if (window.innerWidth > PC_BREAK_POINT) return;
    if (!previousActivatedTabX)
        previousActivatedTabX = getTabWidth(
            activatedTab,
            activatedTabIndex,
            1,
            isRTLService
        );

    const _distanceFromBorder = () => {
        return isRTLService
            ? lastTab.getBoundingClientRect().left
            : lastTab.getBoundingClientRect().right - window.innerWidth;
    };

    const _resizeStartCondition = () => {
        return isRTLService
            ? _distanceFromBorder() <= 0
            : _distanceFromBorder() >= 0;
    };

    if (_resizeStartCondition()) {
        curMove = _distanceFromBorder() - prevMove;

        const _nextMoveCondition = () => {
            const nextMove = horizontalScroll.scrollLeft + curMove;
            return isRTLService
                ? nextMove <= previousActivatedTabX
                : nextMove >= previousActivatedTabX;
        };

        if (_nextMoveCondition()) {
            horizontalScroll.scrollLeft = previousActivatedTabX;
        } else {
            horizontalScroll.scrollLeft += curMove;
        }

        prevMove = _distanceFromBorder();
    }
};

/**
 * @category tab
 * @description 활성화된 탭의 N칸 전까지의 너비 구하는 함수 (width)
 * @param {HTMLElement} activatedTab 활성화된 탭
 * @param {number} activatedTabIndex 활성화된 탭 Index
 * @param {number} offset N칸 설정
 * @param {boolean} isRTLService RTL 서비스의 적용 유무
 * @returns {number} N칸 전까지의 탭 크기
 * @example
 * getTabWidth(document.querySelector('.depth3-list-items.selected'), 3, 2, true);
 */
export const getTabWidth = (
    activatedTab: HTMLElement,
    activatedTabIndex: number,
    offset: number = 1,
    isRTLService: boolean = false
): number => {
    let previousActivatedTabX = 0;
    if (activatedTabIndex - offset >= 0) offset = 1;
    if (window.innerWidth <= PC_BREAK_POINT && activatedTab.parentElement) {
        for (let i = 0; i < activatedTabIndex - offset; i++) {
            previousActivatedTabX +=
                activatedTab.parentElement.children[i].getBoundingClientRect()
                    .width;
        }
    }

    return isRTLService ? -1 * previousActivatedTabX : previousActivatedTabX;
};
